export type LeadLostReason =
  | 'price'
  | 'date_unsuitable'
  | 'needs_finance'
  | 'not_ready'
  | 'no_response'
  | 'lost_to_competitor'
  | 'wanted_cheaper_cpd'
  | 'wanted_regulated_qualification'
  | 'not_eligible'
  | 'confused_about_route'
  | 'insurance_concern'
  | 'location_issue'
  | 'payment_issue'
  | 'duplicate'
  | 'not_interested'
  | 'other';

export const LEAD_LOST_REASONS: {
  value: LeadLostReason;
  label: string;
  hint?: string;
}[] = [
  { value: 'price', label: 'Price', hint: 'Cost or value concern' },
  { value: 'date_unsuitable', label: 'Date unsuitable' },
  { value: 'needs_finance', label: 'Needs finance' },
  { value: 'not_ready', label: 'Not ready' },
  { value: 'no_response', label: 'No response' },
  { value: 'lost_to_competitor', label: 'Lost to competitor' },
  {
    value: 'wanted_cheaper_cpd',
    label: 'Wanted cheaper CPD',
  },
  {
    value: 'wanted_regulated_qualification',
    label: 'Wanted regulated qualification',
  },
  { value: 'not_eligible', label: 'Not eligible' },
  {
    value: 'confused_about_route',
    label: 'Confused about route',
    hint: 'Unclear on the right training path',
  },
  { value: 'insurance_concern', label: 'Insurance concern' },
  { value: 'location_issue', label: 'Location issue' },
  { value: 'payment_issue', label: 'Payment issue' },
  {
    value: 'duplicate',
    label: 'Duplicate',
    hint: 'Same person already exists as another lead or customer',
  },
  {
    value: 'not_interested',
    label: 'Not interested',
    hint: 'Declined training or no longer pursuing',
  },
  {
    value: 'other',
    label: 'Other',
    hint: 'Describe the reason in the notes field',
  },
];

export function getLeadLostReasonLabel(value?: string | null) {
  if (!value) {
    return null;
  }

  return (
    LEAD_LOST_REASONS.find((reason) => reason.value === value)?.label ?? value
  );
}

export function countLeadLostReasons(
  leads: Array<{ lost_reason?: string | null }>,
) {
  const counts = new Map<string, number>();

  for (const reason of LEAD_LOST_REASONS) {
    counts.set(reason.value, 0);
  }

  for (const lead of leads) {
    if (!lead.lost_reason) {
      continue;
    }
    counts.set(lead.lost_reason, (counts.get(lead.lost_reason) ?? 0) + 1);
  }

  return LEAD_LOST_REASONS.map((reason) => ({
    ...reason,
    count: counts.get(reason.value) ?? 0,
  })).filter((row) => row.count > 0);
}

export type LostReasonLead = {
  id: string;
  status?: string | null;
  createdAt?: string | null;
  created_at?: string | null;
  lost_reason?: string | null;
  lost_reason_at?: string | null;
  lost_reason_notes?: string | null;
  content?: {
    name?: string;
    surname?: string;
    email?: string;
  } | null;
  metadata?: { title?: string | null } | null;
};

export function getLeadCreatedAt(lead: LostReasonLead) {
  const raw = lead.createdAt || lead.created_at;
  return raw ? new Date(raw) : null;
}

export function getClosedLeadsInPeriod(
  leads: LostReasonLead[],
  start: Date,
  end: Date,
) {
  return leads.filter((lead) => {
    if (lead.status !== 'CLOSED') {
      return false;
    }

    if (!lead.lost_reason_at) {
      return false;
    }

    const closedAt = new Date(lead.lost_reason_at);
    return closedAt >= start && closedAt <= end;
  });
}

export function filterClosedLeadsByLostReason(
  leads: LostReasonLead[],
  start: Date,
  end: Date,
  reason: LeadLostReason | '__missing__',
) {
  const closedInPeriod = getClosedLeadsInPeriod(leads, start, end);

  if (reason === '__missing__') {
    return closedInPeriod.filter((lead) => !lead.lost_reason);
  }

  return closedInPeriod.filter((lead) => lead.lost_reason === reason);
}

export function getLeadListDisplayName(lead: LostReasonLead) {
  const name = lead.content?.name;
  const surname = lead.content?.surname;

  if (name) {
    return [name, surname].filter(Boolean).join(' ');
  }

  return lead.content?.email || lead.metadata?.title || 'Unknown lead';
}
